import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ROUTES, formatSpades } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  NOTIFICATIONS_UPDATED_EVENT,
  readStoredToken,
  readStoredUser,
  SPADES_UPDATED_EVENT,
} from '../../services/authApi';
import SpadeInfoModal from './SpadeInfoModal';
import AddSpadesModal from './AddSpadesModal';
import FilterPanel from './FilterPanel';
import NotificationPanel from './NotificationPanel';
import SearchDropdown from './SearchDropdown';
import polarAceLogo from '../../assets/images/polar_ace_no_bg.png';
import './HomeNavBar.css';

const NOTIFICATION_REFRESH_INTERVAL_MS = 30000;

/**
 * Home page navigation bar.
 *
 * Enhanced version of the base NavBar with:
 * - Search bar (center) — hidden in minimal mode
 * - Spades balance (clickable to open Add Spades panel)
 * - Notification bell with badge
 * - Profile dropdown with navigation and logout
 *
 * @param {Object} props
 * @param {number} props.spadesBalance - User's current Spades balance
 * @param {number} props.notificationCount - Number of unread notifications
 * @param {string|null} props.userAvatar - User's profile picture URL
 * @param {string} props.userName - User's display name
 * @param {function} props.onSpadesClick - Handler for Spades balance click
 * @param {function} [props.onLogout] - Optional page-specific cleanup before logout
 * @param {boolean} props.minimal - Hide search bar and filter (for profile/settings pages)
 * @returns {JSX.Element}
 */
function HomeNavBar({
  spadesBalance,
  notificationCount,
  userAvatar,
  userName,
  onSpadesClick,
  onSpadesPurchaseComplete,
  onLogout,
  minimal = false,
  onSearch,
  logoRoute = ROUTES.HOME,
  forceDocumentNavigation = false,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSpadeInfoOpen, setIsSpadeInfoOpen] = useState(false);
  const [isAddSpadesOpen, setIsAddSpadesOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [displaySpadesBalance, setDisplaySpadesBalance] = useState(spadesBalance);
  const [displayNotificationCount, setDisplayNotificationCount] = useState(notificationCount);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dropdownRef = useRef(null);
  const filterBtnRef = useRef(null);
  const notificationRef = useRef(null);
  const searchRef = useRef(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    setDisplaySpadesBalance(spadesBalance);
  }, [spadesBalance]);

  useEffect(() => {
    setDisplayNotificationCount(notificationCount);
  }, [notificationCount]);

  /* Close dropdowns when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setIsNotificationOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsNotificationOpen(false);
        setIsSearchOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationOpen || isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen, isNotificationOpen, isSearchOpen]);

  useEffect(() => {
    const handleSpadesUpdated = (event) => {
      const nextBalance = event?.detail?.spadeBalance;
      if (typeof nextBalance === 'number' && !Number.isNaN(nextBalance)) {
        setDisplaySpadesBalance(nextBalance);
      }
    };

    window.addEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    return () => {
      window.removeEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    };
  }, []);

  useEffect(() => {
    const handleNotificationsUpdated = (event) => {
      const nextCount = event?.detail?.unreadCount;
      if (typeof nextCount === 'number' && !Number.isNaN(nextCount)) {
        setDisplayNotificationCount(nextCount);
      }
    };

    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadNotificationUnreadCount = useCallback(async () => {
    const token = readStoredToken();
    if (!token) {
      if (isMountedRef.current) {
        setDisplayNotificationCount(0);
        setNotifications([]);
      }
      return 0;
    }

    try {
      const data = await authApi.getNotificationUnreadCount(token);
      const nextCount = Number(data.unreadCount || 0);
      if (isMountedRef.current) {
        setDisplayNotificationCount(nextCount);
      }
      return nextCount;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setDisplayNotificationCount(0);
      setNotifications([]);
      return undefined;
    }

    loadNotificationUnreadCount();
    const intervalId = window.setInterval(
      loadNotificationUnreadCount,
      NOTIFICATION_REFRESH_INTERVAL_MS
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotificationUnreadCount();
      }
    };

    const handleWindowFocus = () => {
      loadNotificationUnreadCount();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loadNotificationUnreadCount]);

  const loadNotifications = useCallback(async () => {
    const token = readStoredToken();
    if (!token) {
      setNotifications([]);
      setDisplayNotificationCount(0);
      return;
    }

    setIsNotificationsLoading(true);
    setNotificationsError('');

    try {
      const data = await authApi.getNotifications({ limit: 30 }, token);
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setNotificationsError('Could not load notifications.');
    } finally {
      setIsNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (minimal) {
      setSearchResults([]);
      setIsSearchOpen(false);
      setIsSearchLoading(false);
      return undefined;
    }

    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) {
      setSearchResults([]);
      setIsSearchOpen(false);
      setIsSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearchLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await authApi.searchUsers(trimmedQuery);
        if (cancelled) return;
        setSearchResults(Array.isArray(results) ? results : []);
        setIsSearchOpen(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to search users:', error);
        setSearchResults([]);
        setIsSearchOpen(true);
      } finally {
        if (!cancelled) {
          setIsSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [minimal, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery && onSearch) {
      onSearch(trimmedQuery);
    }

    if (trimmedQuery.length >= 2) {
      setIsSearchOpen(true);
    }
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (onSearch) {
      onSearch(val.trim());
    }
    if (val.trim().length >= 2) {
      setIsSearchOpen(true);
    }
  };

  const handleNotificationClick = () => {
    setIsNotificationOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen) {
        loadNotificationUnreadCount();
        loadNotifications();
      }
      return nextOpen;
    });
  };

  const handleMarkNotificationRead = async (notification) => {
    if (!notification?.id || notification.isRead) {
      return;
    }

    try {
      const data = await authApi.markNotificationRead(notification.id);
      setDisplayNotificationCount(Number(data.unreadCount || 0));
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id
          ? { ...item, isRead: true, readAt: new Date().toISOString() }
          : item
      )));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const handleClearNotifications = async () => {
    try {
      const data = await authApi.clearNotifications();
      setDisplayNotificationCount(Number(data.unreadCount || 0));
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleNotificationNavigate = async (notification) => {
    await handleMarkNotificationRead(notification);

    const actionUrl = typeof notification?.actionUrl === 'string' ? notification.actionUrl.trim() : '';
    if (!actionUrl) {
      return;
    }

    setIsNotificationOpen(false);
    if (actionUrl.startsWith('/')) {
      if (forceDocumentNavigation) {
        window.location.assign(actionUrl);
        return;
      }
      navigate(actionUrl);
      return;
    }

    window.location.assign(actionUrl);
  };

  const handleSpadeIconClick = () => {
    setIsSpadeInfoOpen(true);
  };

  const handleSpadesBalanceClick = () => {
    setIsAddSpadesOpen(true);
    onSpadesClick();
  };

  const handleGetSpadesFromInfo = () => {
    setIsAddSpadesOpen(true);
  };

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleApplyFilters = (filters) => {
    console.log('Filters applied:', filters);
    // Would apply filters to stream results
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);

    const initialToken = readStoredToken();

    try {
      await onLogout?.();
    } catch (error) {
      console.error('Logout handler failed:', error);
    }

    const remainingToken = readStoredToken();
    const remainingUser = readStoredUser();

    if (remainingToken || remainingUser || !onLogout) {
      await authApi.logout(remainingToken || initialToken || undefined);
      clearStoredAuth();
      if (forceDocumentNavigation) {
        window.location.assign(ROUTES.WELCOME);
        return;
      }
      navigate(ROUTES.WELCOME);
    }
  };

  const renderRouteLink = (path, className, children, extraProps = {}) => {
    if (forceDocumentNavigation) {
      return (
        <a href={path} className={className} {...extraProps}>
          {children}
        </a>
      );
    }

    return (
      <Link to={path} className={className} {...extraProps}>
        {children}
      </Link>
    );
  };

  const formatNotificationCount = (count) => {
    if (count > 999) return '999+';
    return count.toString();
  };

  const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );

  const SettingsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );

  const BellIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 17h5l-1.4-1.4a2 2 0 0 1-.6-1.4V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
      <path d="M10 17a2 2 0 0 0 4 0" />
    </svg>
  );

  return (
    <nav className="home-nav" role="navigation" aria-label="Main navigation">
      {/* Left Section: Logo */}
      <div className="home-nav__left">
        {renderRouteLink(
          logoRoute,
          'home-nav__logo',
          <img src={polarAceLogo} alt="Polar Ace" />
        )}
      </div>

      {/* Center Section: Search — hidden in minimal mode */}
      {!minimal && (
        <div className="home-nav__center">
          <div className="home-nav__search-wrapper" ref={searchRef}>
            <form className="home-nav__search" onSubmit={handleSearch}>
              <span className="home-nav__search-icon">
                <SearchIcon />
              </span>
              <input
                type="text"
                className="home-nav__search-input"
                placeholder="Search for a Streamer"
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={() => {
                  if (searchQuery.trim().length >= 2) {
                    setIsSearchOpen(true);
                  }
                }}
                aria-label="Search for a streamer"
              />
            </form>
            <SearchDropdown
              results={searchResults}
              isLoading={isSearchLoading}
              isVisible={isSearchOpen}
              query={searchQuery.trim()}
              onClose={() => setIsSearchOpen(false)}
            />
          </div>
            <button
              type="button"
              className="home-nav__icon-btn"
              onClick={handleSearch}
              aria-label="Search"
            >
              <SearchIcon />
            </button>
            <div className="home-nav__filter-wrapper" ref={filterBtnRef}>
              <button
              type="button"
                className="home-nav__icon-btn"
                onClick={handleFilterClick}
                aria-label="Filter"
                aria-expanded={isFilterOpen}
              >
                <SettingsIcon />
              </button>
            <FilterPanel
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              onApplyFilters={handleApplyFilters}
            />
          </div>
        </div>
      )}

      {/* Right Section: Spades, Notifications, Profile */}
      <div className="home-nav__right">
        {/* Spades Balance — split: icon opens info, balance opens purchase */}
        <div className="home-nav__spades">
          <button
            type="button"
            className="home-nav__spades-info"
            onClick={handleSpadeIconClick}
            aria-label="What is a Spade?"
          >
            <span className="home-nav__spades-icon" aria-hidden="true">♠</span>
          </button>
          <button
            type="button"
            className="home-nav__spades-balance"
            onClick={handleSpadesBalanceClick}
            aria-label={`${formatSpades(displaySpadesBalance)} Spades. Click to add more.`}
          >
            {formatSpades(displaySpadesBalance)}
          </button>
        </div>

        {/* Notifications */}
        <div className="home-nav__notification-wrapper" ref={notificationRef}>
          <button
            type="button"
            className="home-nav__notification"
            onClick={handleNotificationClick}
            aria-label={`${displayNotificationCount} notifications`}
            aria-expanded={isNotificationOpen}
          >
            <span className="home-nav__notification-icon">
              <BellIcon />
            </span>
            {displayNotificationCount > 0 && (
              <span className="home-nav__notification-badge">
                {formatNotificationCount(displayNotificationCount)}
              </span>
            )}
          </button>
          <NotificationPanel
            isOpen={isNotificationOpen}
            notifications={notifications}
            isLoading={isNotificationsLoading}
            errorMessage={notificationsError}
            unreadCount={displayNotificationCount}
            onClearAll={handleClearNotifications}
            onNotificationClick={handleNotificationNavigate}
          />
        </div>

        {/* Profile Dropdown */}
        <div className="home-nav__profile" ref={dropdownRef}>
          <button
            type="button"
            className="home-nav__avatar"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            aria-label="User menu"
          >
            {userAvatar ? (
              <img src={userAvatar} alt="" />
            ) : (
              <span className="home-nav__avatar-placeholder">
                {userName.charAt(0).toUpperCase()}
              </span>
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="home-nav__dropdown" role="menu">
              {renderRouteLink(
                ROUTES.PROFILE,
                'home-nav__dropdown-item',
                'Your Profile',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.SETTINGS,
                'home-nav__dropdown-item',
                'Settings',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.WALLET,
                'home-nav__dropdown-item',
                'Wallet',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.SUPPORT,
                'home-nav__dropdown-item',
                'Support',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.REPORTS,
                'home-nav__dropdown-item',
                'Report History',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              <button
                type="button"
                className="home-nav__dropdown-item home-nav__dropdown-item--logout"
                role="menuitem"
                onClick={handleLogout}
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <SpadeInfoModal
        isOpen={isSpadeInfoOpen}
        onClose={() => setIsSpadeInfoOpen(false)}
        onGetSpades={handleGetSpadesFromInfo}
      />

      <AddSpadesModal
        isOpen={isAddSpadesOpen}
        onClose={() => setIsAddSpadesOpen(false)}
        onPurchaseComplete={(result) => {
          setIsAddSpadesOpen(false);
          onSpadesPurchaseComplete?.(result);
        }}
      />
    </nav>
  );
}

HomeNavBar.propTypes = {
  spadesBalance: PropTypes.number.isRequired,
  notificationCount: PropTypes.number.isRequired,
  userAvatar: PropTypes.string,
  userName: PropTypes.string.isRequired,
  onSpadesClick: PropTypes.func.isRequired,
  onSpadesPurchaseComplete: PropTypes.func,
  onLogout: PropTypes.func,
  minimal: PropTypes.bool,
  onSearch: PropTypes.func,
  logoRoute: PropTypes.string,
  forceDocumentNavigation: PropTypes.bool,
};

export default HomeNavBar;
